import { useState } from 'react';
import { X, Sparkles, Plus, Trash2, ChevronDown, Check, AlertCircle } from 'lucide-react';
import { useWizardStore } from '../../stores/wizardStore';
import { useWizardOptions } from '../../hooks/useWizardOptions';
import { analyzeCode, type CodeSample, type CodeAnalysisResult } from '../../lib/api';
import clsx from 'clsx';

type AnalysisState = 'idle' | 'adding-samples' | 'analyzing' | 'review-results' | 'applied';

export default function CodingStandardsStep() {
  const { codingStandards, updateCodingStandards } = useWizardStore();
  const { options, loading } = useWizardOptions();
  const [pathInput, setPathInput] = useState('');

  // Code analysis state
  const [analysisState, setAnalysisState] = useState<AnalysisState>('idle');
  const [samples, setSamples] = useState<CodeSample[]>([{ filename: '', content: '' }]);
  const [analysisResult, setAnalysisResult] = useState<CodeAnalysisResult | null>(null);
  const [analysisError, setAnalysisError] = useState<string | null>(null);

  const lintingOptions = options?.codingStandards.lintingOptions ?? [];
  const formattingOptions = options?.codingStandards.formattingOptions ?? [];
  const testRequirementOptions = options?.codingStandards.testRequirementOptions ?? [];
  const architecturePatterns = options?.codingStandards.architecturePatterns ?? [];
  const dependencyPolicies = options?.codingStandards.dependencyPolicies ?? [];
  const namingConventionOptions = options?.codingStandards.namingConventionOptions ?? [];

  if (loading) return <div className="text-gray-500 text-sm py-8 text-center">Loading configuration...</div>;

  const toggleTestReq = (value: string) => {
    const current = codingStandards.testRequirements;
    if (current.includes(value)) {
      updateCodingStandards({ testRequirements: current.filter((r) => r !== value) });
    } else {
      updateCodingStandards({ testRequirements: [...current, value] });
    }
  };

  const toggleNaming = (value: string) => {
    const current = codingStandards.namingConventions;
    if (current.includes(value)) {
      updateCodingStandards({ namingConventions: current.filter((n) => n !== value) });
    } else {
      updateCodingStandards({ namingConventions: [...current, value] });
    }
  };

  const addSensitivePath = () => {
    const trimmed = pathInput.trim();
    if (trimmed && !codingStandards.sensitivePaths.includes(trimmed)) {
      updateCodingStandards({
        sensitivePaths: [...codingStandards.sensitivePaths, trimmed],
      });
      setPathInput('');
    }
  };

  const removeSensitivePath = (path: string) => {
    updateCodingStandards({
      sensitivePaths: codingStandards.sensitivePaths.filter((p) => p !== path),
    });
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-semibold text-white mb-1">
          Coding Standards
        </h2>
        <p className="text-sm text-gray-500">
          Define the quality bar for all code -- human and AI-generated.
        </p>
      </div>

      {/* Auto-detect from Code */}
      <div className="border border-dark-border rounded-lg overflow-hidden">
        <button
          onClick={() => {
            if (analysisState === 'idle') {
              setAnalysisState('adding-samples');
              setAnalysisError(null);
            } else if (analysisState === 'adding-samples' || analysisState === 'review-results' || analysisState === 'applied') {
              setAnalysisState('idle');
            }
          }}
          className="w-full flex items-center justify-between px-4 py-3 bg-surface-DEFAULT hover:bg-dark-hover transition-colors"
        >
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-amber-400" />
            <span className="text-sm font-medium text-white">Auto-detect from Code</span>
            {analysisState === 'applied' && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/10 text-green-400 border border-green-500/20">
                Applied
              </span>
            )}
          </div>
          <ChevronDown className={clsx(
            'w-4 h-4 text-gray-500 transition-transform',
            analysisState !== 'idle' && 'rotate-180'
          )} />
        </button>

        {analysisState !== 'idle' && (
          <div className="px-4 py-4 border-t border-dark-border space-y-4">
            {analysisState === 'applied' && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-green-500/10 border border-green-500/20">
                <Check className="w-4 h-4 text-green-400 shrink-0" />
                <p className="text-sm text-green-400">Suggestions applied! Review the fields below.</p>
              </div>
            )}

            {analysisState === 'adding-samples' && (
              <>
                <p className="text-xs text-gray-500">
                  Paste 1-5 code samples from your project. AI will analyze them to detect your coding conventions.
                </p>
                <div className="space-y-3">
                  {samples.map((sample, i) => (
                    <div key={i} className="border border-dark-border rounded-lg p-3 space-y-2">
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={sample.filename}
                          onChange={(e) => {
                            const next = [...samples];
                            next[i] = { ...next[i], filename: e.target.value };
                            setSamples(next);
                          }}
                          placeholder="filename (e.g. UserService.ts)"
                          className="input-field flex-1 text-sm py-1.5"
                        />
                        {samples.length > 1 && (
                          <button
                            onClick={() => setSamples(samples.filter((_, j) => j !== i))}
                            className="text-gray-500 hover:text-red-400 transition-colors p-1"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <textarea
                        value={sample.content}
                        onChange={(e) => {
                          const next = [...samples];
                          next[i] = { ...next[i], content: e.target.value.slice(0, 10000) };
                          setSamples(next);
                        }}
                        placeholder="Paste code here..."
                        rows={6}
                        className="input-field w-full resize-y text-xs font-mono"
                      />
                      <div className="flex justify-end">
                        <span className="text-xs text-gray-600">{sample.content.length} / 10,000</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-3">
                  {samples.length < 5 && (
                    <button
                      onClick={() => setSamples([...samples, { filename: '', content: '' }])}
                      className="btn-secondary text-xs flex items-center gap-1 px-3 py-1.5"
                    >
                      <Plus className="w-3 h-3" /> Add Sample
                    </button>
                  )}
                  <button
                    onClick={async () => {
                      const validSamples = samples.filter(s => s.filename.trim() && s.content.trim().length >= 10);
                      if (validSamples.length === 0) {
                        setAnalysisError('Add at least one code sample with a filename and at least 10 characters of code.');
                        return;
                      }
                      setAnalysisError(null);
                      setAnalysisState('analyzing');
                      try {
                        const result = await analyzeCode(validSamples);
                        setAnalysisResult(result);
                        setAnalysisState('review-results');
                      } catch (err: any) {
                        setAnalysisError(err.message || 'Analysis failed');
                        setAnalysisState('adding-samples');
                      }
                    }}
                    disabled={samples.every(s => !s.content.trim())}
                    className="btn-primary text-xs flex items-center gap-1 px-4 py-1.5 disabled:opacity-50"
                  >
                    <Sparkles className="w-3 h-3" /> Analyze Code
                  </button>
                </div>
                {analysisError && (
                  <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                    <AlertCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                    <p className="text-sm text-red-400">{analysisError}</p>
                  </div>
                )}
              </>
            )}

            {analysisState === 'analyzing' && (
              <div className="flex items-center justify-center gap-3 py-8">
                <div className="w-5 h-5 border-2 border-dark-border border-t-accent rounded-full animate-spin" />
                <span className="text-sm text-gray-400">Analyzing your code...</span>
              </div>
            )}

            {analysisState === 'review-results' && analysisResult && (
              <>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-white">Detected Conventions</span>
                    <span className={clsx(
                      'text-xs px-2 py-0.5 rounded-full border',
                      analysisResult.confidence === 'high' ? 'bg-green-500/10 text-green-400 border-green-500/20' :
                      analysisResult.confidence === 'medium' ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' :
                      'bg-gray-500/10 text-gray-400 border-gray-500/20'
                    )}>
                      {analysisResult.confidence} confidence
                    </span>
                  </div>
                </div>

                <p className="text-xs text-gray-500">{analysisResult.reasoning}</p>

                <div className="space-y-2 text-sm">
                  {analysisResult.suggestions.linting && (
                    <div className="flex justify-between py-1.5 border-b border-dark-border">
                      <span className="text-gray-500">Linting</span>
                      <span className="text-gray-300">{analysisResult.suggestions.linting}</span>
                    </div>
                  )}
                  {analysisResult.suggestions.formatting && (
                    <div className="flex justify-between py-1.5 border-b border-dark-border">
                      <span className="text-gray-500">Formatting</span>
                      <span className="text-gray-300">{analysisResult.suggestions.formatting}</span>
                    </div>
                  )}
                  {analysisResult.suggestions.architecture && (
                    <div className="flex justify-between py-1.5 border-b border-dark-border">
                      <span className="text-gray-500">Architecture</span>
                      <span className="text-gray-300">{analysisResult.suggestions.architecture}</span>
                    </div>
                  )}
                  {analysisResult.suggestions.coverageThreshold != null && (
                    <div className="flex justify-between py-1.5 border-b border-dark-border">
                      <span className="text-gray-500">Coverage</span>
                      <span className="text-gray-300">{analysisResult.suggestions.coverageThreshold}%</span>
                    </div>
                  )}
                  {analysisResult.suggestions.maxFileLength != null && (
                    <div className="flex justify-between py-1.5 border-b border-dark-border">
                      <span className="text-gray-500">Max File Length</span>
                      <span className="text-gray-300">{analysisResult.suggestions.maxFileLength} lines</span>
                    </div>
                  )}
                  {analysisResult.suggestions.dependencyPolicy && (
                    <div className="flex justify-between py-1.5 border-b border-dark-border">
                      <span className="text-gray-500">Dependency Policy</span>
                      <span className="text-gray-300">{analysisResult.suggestions.dependencyPolicy}</span>
                    </div>
                  )}
                  {analysisResult.suggestions.testRequirements.length > 0 && (
                    <div className="flex justify-between py-1.5 border-b border-dark-border">
                      <span className="text-gray-500 shrink-0">Tests</span>
                      <div className="flex flex-wrap gap-1 justify-end">
                        {analysisResult.suggestions.testRequirements.map(t => (
                          <span key={t} className="text-xs px-2 py-0.5 rounded bg-surface-100 border border-dark-border text-gray-300">{t}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {analysisResult.suggestions.namingConventions.length > 0 && (
                    <div className="flex justify-between py-1.5 border-b border-dark-border">
                      <span className="text-gray-500 shrink-0">Naming</span>
                      <div className="flex flex-wrap gap-1 justify-end">
                        {analysisResult.suggestions.namingConventions.map(n => (
                          <span key={n} className="text-xs px-2 py-0.5 rounded bg-surface-100 border border-dark-border text-gray-300 font-mono">{n}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {analysisResult.suggestions.sensitivePaths.length > 0 && (
                    <div className="flex justify-between py-1.5 border-b border-dark-border">
                      <span className="text-gray-500 shrink-0">Sensitive Paths</span>
                      <div className="flex flex-wrap gap-1 justify-end">
                        {analysisResult.suggestions.sensitivePaths.map(p => (
                          <span key={p} className="text-xs px-2 py-0.5 rounded bg-red-500/10 border border-red-500/20 text-red-400">{p}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {analysisResult.suggestions.customConventions && (
                    <div className="py-1.5">
                      <span className="text-gray-500 text-xs">Custom Conventions</span>
                      <p className="text-gray-300 text-xs mt-1 whitespace-pre-wrap">{analysisResult.suggestions.customConventions}</p>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-3 pt-2">
                  <button
                    onClick={() => {
                      const s = analysisResult.suggestions;
                      updateCodingStandards({
                        ...(s.linting ? { linting: s.linting } : {}),
                        ...(s.formatting ? { formatting: s.formatting } : {}),
                        ...(s.architecture ? { architecture: s.architecture } : {}),
                        ...(s.coverageThreshold != null ? { coverageThreshold: s.coverageThreshold } : {}),
                        ...(s.maxFileLength != null ? { maxFileLength: s.maxFileLength } : {}),
                        ...(s.dependencyPolicy ? { dependencyPolicy: s.dependencyPolicy } : {}),
                        ...(s.testRequirements.length > 0 ? { testRequirements: s.testRequirements } : {}),
                        ...(s.namingConventions.length > 0 ? { namingConventions: s.namingConventions } : {}),
                        ...(s.sensitivePaths.length > 0 ? { sensitivePaths: s.sensitivePaths } : {}),
                        ...(s.customConventions ? { customConventions: s.customConventions } : {}),
                      });
                      setAnalysisState('applied');
                    }}
                    className="btn-primary text-xs flex items-center gap-1 px-4 py-1.5"
                  >
                    <Check className="w-3 h-3" /> Apply All Suggestions
                  </button>
                  <button
                    onClick={() => {
                      setAnalysisState('adding-samples');
                      setAnalysisResult(null);
                    }}
                    className="btn-secondary text-xs px-3 py-1.5"
                  >
                    Re-analyze
                  </button>
                  <button
                    onClick={() => {
                      setAnalysisState('idle');
                      setAnalysisResult(null);
                    }}
                    className="text-xs text-gray-500 hover:text-gray-300 transition-colors px-2 py-1.5"
                  >
                    Dismiss
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Linting */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Linting Rules
        </label>
        <select
          value={codingStandards.linting}
          onChange={(e) => updateCodingStandards({ linting: e.target.value })}
          className="select-field"
        >
          <option value="">Select linting configuration...</option>
          {lintingOptions.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      </div>

      {/* Formatting */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Formatting
        </label>
        <select
          value={codingStandards.formatting}
          onChange={(e) => updateCodingStandards({ formatting: e.target.value })}
          className="select-field"
        >
          <option value="">Select code formatter...</option>
          {formattingOptions.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      </div>

      {/* Test Requirements */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-3">
          Test Requirements
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
          {testRequirementOptions.map((option) => (
            <label
              key={option}
              className={clsx(
                'flex items-center gap-3 px-4 py-3 rounded-lg border cursor-pointer transition-all duration-200 text-sm',
                codingStandards.testRequirements.includes(option)
                  ? 'bg-accent/10 border-accent/30 text-accent-300'
                  : 'bg-surface-DEFAULT border-dark-border text-gray-400 hover:border-surface-300'
              )}
            >
              <input
                type="checkbox"
                checked={codingStandards.testRequirements.includes(option)}
                onChange={() => toggleTestReq(option)}
                className="sr-only"
              />
              <div
                className={clsx(
                  'w-4 h-4 rounded border flex items-center justify-center shrink-0',
                  codingStandards.testRequirements.includes(option)
                    ? 'bg-accent border-accent'
                    : 'border-surface-300 bg-surface-DEFAULT'
                )}
              >
                {codingStandards.testRequirements.includes(option) && (
                  <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
              {option}
            </label>
          ))}
        </div>

        {/* Coverage Threshold Slider */}
        <div className="bg-surface-DEFAULT rounded-lg p-4 border border-dark-border">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-gray-400">
              Coverage Threshold
            </span>
            <span className="text-sm font-semibold text-accent">
              {codingStandards.coverageThreshold}%
            </span>
          </div>
          <input
            type="range"
            min={0}
            max={100}
            step={5}
            value={codingStandards.coverageThreshold}
            onChange={(e) =>
              updateCodingStandards({
                coverageThreshold: parseInt(e.target.value, 10),
              })
            }
            className="w-full h-1.5 bg-surface-200 rounded-full appearance-none cursor-pointer
              [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4
              [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-accent
              [&::-webkit-slider-thumb]:shadow-lg [&::-webkit-slider-thumb]:shadow-accent/30
              [&::-webkit-slider-thumb]:cursor-pointer"
          />
          <div className="flex justify-between text-xs text-gray-600 mt-1">
            <span>0%</span>
            <span>50%</span>
            <span>100%</span>
          </div>
        </div>
      </div>

      {/* Architecture Pattern */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Architecture Pattern
        </label>
        <select
          value={codingStandards.architecture}
          onChange={(e) =>
            updateCodingStandards({ architecture: e.target.value })
          }
          className="select-field"
        >
          <option value="">Select architecture pattern...</option>
          {architecturePatterns.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
      </div>

      {/* Sensitive Paths */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Sensitive Paths
        </label>
        <p className="text-xs text-gray-500 mb-3">
          Paths that require human review and should never be modified by AI
          alone.
        </p>
        <div className="flex gap-2 mb-3">
          <input
            type="text"
            value={pathInput}
            onChange={(e) => setPathInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addSensitivePath()}
            placeholder="e.g., auth/*, payments/*, infra/*"
            className="input-field flex-1"
          />
          <button onClick={addSensitivePath} className="btn-secondary px-4">
            Add
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {codingStandards.sensitivePaths.map((path) => (
            <span
              key={path}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500/10 border border-red-500/20 text-sm text-red-400"
            >
              {path}
              <button
                onClick={() => removeSensitivePath(path)}
                className="text-red-400/60 hover:text-red-400 transition-colors"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      </div>

      {/* Dependency Policy */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Dependency Policy
        </label>
        <select
          value={codingStandards.dependencyPolicy}
          onChange={(e) =>
            updateCodingStandards({ dependencyPolicy: e.target.value })
          }
          className="select-field"
        >
          <option value="">Select dependency policy...</option>
          {dependencyPolicies.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
      </div>

      {/* Max File Length */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Max File Length (lines)
        </label>
        <input
          type="number"
          value={codingStandards.maxFileLength}
          onChange={(e) =>
            updateCodingStandards({
              maxFileLength: parseInt(e.target.value, 10) || 300,
            })
          }
          min={50}
          max={1000}
          className="input-field w-40"
        />
        <p className="mt-1.5 text-xs text-gray-600">
          Recommended: 200-400 lines per file
        </p>
      </div>

      {/* Naming Conventions */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-3">
          Naming Conventions
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {namingConventionOptions.map((option) => (
            <label
              key={option}
              className={clsx(
                'flex items-center gap-3 px-4 py-3 rounded-lg border cursor-pointer transition-all duration-200 text-sm',
                codingStandards.namingConventions.includes(option)
                  ? 'bg-accent/10 border-accent/30 text-accent-300'
                  : 'bg-surface-DEFAULT border-dark-border text-gray-400 hover:border-surface-300'
              )}
            >
              <input
                type="checkbox"
                checked={codingStandards.namingConventions.includes(option)}
                onChange={() => toggleNaming(option)}
                className="sr-only"
              />
              <div
                className={clsx(
                  'w-4 h-4 rounded border flex items-center justify-center shrink-0',
                  codingStandards.namingConventions.includes(option)
                    ? 'bg-accent border-accent'
                    : 'border-surface-300 bg-surface-DEFAULT'
                )}
              >
                {codingStandards.namingConventions.includes(option) && (
                  <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
              <span className="font-mono text-xs">{option}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Project-Specific Conventions */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Project-Specific Conventions
        </label>
        <p className="text-xs text-gray-500 mb-3">
          Describe any additional coding conventions specific to your project.
          These will be included verbatim in the generated coding style rules.
        </p>
        <textarea
          value={codingStandards.customConventions}
          onChange={(e) =>
            updateCodingStandards({ customConventions: e.target.value.slice(0, 2000) })
          }
          placeholder={`e.g.,\n- All API responses use { data, error, meta } envelope pattern\n- React components must be functional with hooks only\n- Database queries go through repository pattern, never direct ORM calls\n- Error codes follow DOMAIN_ACTION_REASON format (e.g. AUTH_LOGIN_INVALID_CREDENTIALS)`}
          rows={5}
          className="input-field w-full resize-y text-sm"
        />
        <div className="flex justify-end mt-1">
          <span className={clsx(
            'text-xs',
            codingStandards.customConventions.length > 1800 ? 'text-amber-400' : 'text-gray-600'
          )}>
            {codingStandards.customConventions.length} / 2000
          </span>
        </div>
      </div>
    </div>
  );
}
